import {connect} from 'pwa-helpers';
import {LitElement, html, css} from 'lit';
import {navigator} from 'lit-element-router';
import {customElement} from 'lit/decorators/custom-element.js';
import {property} from 'lit/decorators/property.js';
import {classMap} from 'lit/directives/class-map.js';
import {when} from 'lit/directives/when.js';
import {msg, updateWhenLocaleChanges} from '@lit/localize';
import store from '../store';
import {
	setMedia,
	setSection,
	nextSection,
	previousSection,
	setHighlightFeatures
} from '../slices/statusSlice';

import next from '../../icons/next';
import previous from '../../icons/previous';
import checkmark from '../../icons/checkmark';
import {selectCurrentSection, selectFeatureModelCompletionPercentage, selectSectionConfig, selectUnsatisfied, selectLightProfileIsSatisfied, selectLuminaryIsSatisfied} from '../selectors';
import {routeUrl} from '../util/routeUrl';

@customElement('anwendung-nav')
export class anwendungNav extends connect(store)(navigator(LitElement)) {

	static styles = css`

		:host {
			position: initial;
	  	}

		.applicationSectionNav {
			position: absolute;
			right: 0px;
			bottom: 0px;
			left: 0px;
			background-image: linear-gradient(0deg, rgba(0,0,0,1) 67%, rgba(0,0,0,0) 100%);
			padding: var(--small-padding);
		}

		.arrows {
			display: flex;
			gap: var(--small-padding);
			align-items: flex-end;
			color: var(--color-text-sections);
			padding: 0;
			position: relative;
			float: right;
			z-index: 20;
			bottom: 0;
			right: 0;
		}

		.arrow {
			flex-grow: 0;
			width: var(--sections-progress-height);
			height: var(--sections-progress-height);
			color: var(--color-grey4);
			transition: background-color 0.4s, color 0.4s, border-voler 0.4s;
			border: 1px solid var(--color-grey5);
			pointer-events: none;
			font-size: var(--font-size-sections);
			background-color: var(--color-black);
			cursor: pointer;
		}

	    .arrow svg {
	  		height: 12px;
	  		width: auto;
	    }

		.arrow svg * {
			stroke: currentColor;
		}

		.arrow.active {
			border: 1px solid var(--color-white);
			color: var(--color-white);
			pointer-events: all;
		}

		.icon {
			pointer-events: none;
			width: 11px;
			height: 11px;
			display: inline-block;
			color: var(--color-white);
		}

		.icon svg {
			display: block;
			fill: currentColor
		}

		.icon.satisfied {
			color: var(--color-text-widget-title);
		}

		button {
			font-family: var(--font-family-light);
			font-size: var(--font-size-widget);
			color: var(--color-text-widget-title);
			height: var(--sections-progress-height);
			text-transform: uppercase;
			letter-spacing: 1px;
			transition: background-color 0.4s, color 0.4s;
			background-color: transparent;
			border: none;
			cursor: pointer;
			line-height: 0;
	 	}

	    .choice {
			border: 1px solid var(--color-white);
			text-transform: none;
			font: var(--font-summary-table);
			padding: 0 var(--very-small-padding);
			background-color: var(--color-black);
	  	}

	 	 .arrow.choice svg {
			padding: 0px;
			padding-left: var(--very-small-padding);
	  	}

	  	button:disabled, button[disabled] {
			border: 1px solid var(--color-grey5);
			color: var(	--color-grey5);
			cursor: default;
	 	}

	 	button[disabled] svg {
			stroke: var(	--color-grey5);
			cursor: default;
	  	}

		.noLuminare {
			position: relative;
			float: left;
			left: 0px;
			padding: 5px;
			max-height: 90px;
		}

		.noLuminare svg {
			padding: 0 var(--very-small-padding);
		}

		.desktop-only {
			display: none;
		}


	  @media (min-width: 768px) {

		:host {
			position: absolute;
    		right: 0px;
    		top: 55%;
    		display: flex;
		}

		.desktop-only {
			display: block;
			width: 100%;
			text-align: left;
		}

		.applicationSectionNav {
			position: relative;
			padding: var(--very-large-padding) var(--large-padding);
			margin-top: 5%;
			background-image: none;
		}

		.arrows {
			margin-bottom: var(--column-gap);
			position: initial;
			float: none;
		}

		.arrow {
			background-color: transparent;
		}

		.arrow.choice {
			width: 290px;
			display: flex;
			padding: var(--small-padding);
		}

		.noLuminare {
			position: initial;
			max-height: 90px;
			padding: var(--very-small-padding);
			float: right;
			width: 290px;
			text-align: left;
		}

		.noLuminare svg {
			float: right;
			padding: 0px;
		}

	  }

	`;

	@property({type: String})
	currentSection = '';

	constructor() {
		super();
		updateWhenLocaleChanges(this);
		store.subscribe(() => this.requestUpdate());
	}

	connectedCallback() {
		super.connectedCallback();
		// addEventListener('keydown', this.handleKeydown);
	}

	stateChanged(state) {
		this.state = state;
		this.productData = state.productData.productData;
		this.lang = state.status.lang;
		this.sectionConfig = selectSectionConfig(state);
		this.currentSection = selectCurrentSection(state);
		this.dashboardActive = state.status.dashboardActive;
		this.specsheetActive = state.status.specsheetActive;
		this.visualActive = state.status.visualActive;
		this.features = state.featureModel.data.features ?? [];
		this.decisions = state.decisions.data;
		this.featureModelIsSatisfied = state.decisions.data.isSatisfied;
	}

	setSection(e) {
		store.dispatch(setSection({currentSection: e.target.getAttribute('key')}));
		store.dispatch(setMedia({media: 'sections'}));
	}

	previousSection() {
		const currentIndex = this.sectionConfig.findIndex(section => section.id === this.currentSection);
		const newIndex = currentIndex > 0 ? currentIndex - 1 : 0;
		const newSection = this.sectionConfig[newIndex].id;

		store.dispatch(setSection({currentSection: newSection}));
		store.dispatch(setMedia({media: 'sections'}));
	}

	nextSection() {
		const currentIndex = this.sectionConfig.findIndex(section => section.id === this.currentSection);
		const newIndex = currentIndex < this.sectionConfig.length - 1 ? currentIndex + 1 : this.sectionConfig.length - 1;
		const newSection = this.sectionConfig[newIndex].id;

		if (currentIndex < this.sectionConfig.length - 1) {
			store.dispatch(setSection({currentSection: newSection}));
			store.dispatch(setMedia({media: 'sections'}));
		} else if (this.featureModelIsSatisfied) {
			this.openSpecsheet();
		}
	}
	openSpecsheet(e) {
		if (this.featureModelIsSatisfied) {
			this.navigate(routeUrl({media: 'specsheet'}));
		} else {
			const unsatisfied = selectUnsatisfied(this.state);
			if (unsatisfied && unsatisfied.length > 0) {
				store.dispatch(setSection({ currentSection: unsatisfied[0].sectionId}));
				store.dispatch(setHighlightFeatures({ highlightFeatures: unsatisfied.reduce( (acc, s) => acc.concat(s.unsatisfiedFeatures), [])}));
			}
		}
	}
	closeSpecsheet(e) {
		this.navigate(routeUrl({media: this.forceOverview ? 'overview' : 'sections'}));
	}

	toggleSpecsheet(e) {
		if (this.featureModelIsSatisfied) {
			if (this.specsheetActive) {
				this.navigate(routeUrl({media: this.forceOverview ? 'overview' : 'sections'}));
			} else {
				if (this.state.status.family === 'LightProfile') {
					// TODO: not hardcoded configuration of summary/specsheet component
					this.navigate(routeUrl({media: this.state.status.media === 'summary' ? 'sections' : 'summary'}));
				} else {
					this.navigate(routeUrl({media: 'specsheet'}));
				}
			}
		} else {
			if (this.state.status.family === 'LightProfile') {
				// TODO: not hardcoded configuration of summary/specsheet component
				this.navigate(routeUrl({media: this.state.status.media === 'summary' ? 'sections' : 'summary'}));
			} else {
				const unsatisfied = selectUnsatisfied(this.state);
				if (unsatisfied && unsatisfied.length > 0) {
					store.dispatch(setSection({currentSection: unsatisfied[0].sectionId}));
					store.dispatch(setHighlightFeatures({highlightFeatures: unsatisfied.reduce((acc, s) => acc.concat(s.unsatisfiedFeatures), [])}));
				}
			}
		}
	}

	handleKeydown = (e) => {
		switch (e.keyCode) {
			case 39: // cursor right
				e.preventDefault();
				this.nextSection();
				break;
			case 37:  // cursor left
				e.preventDefault();
				this.previousSection();
				break;
		}
	};

	render() {
		if (this.dashboardActive || this.specsheetActive) return null;
		const currentIndex = this.sectionConfig.findIndex(section => section.id === this.currentSection);

		const isActive = selectLightProfileIsSatisfied(this.state);
		const isButtonDisabled = !isActive;


			return html`
			${when(this.specsheetActive,
				() => html`
					<button @click="${this.toggleSpecsheet}" class="close">
						${close}
					</button>`,
				() => html`
						${when(this.state.status.family === 'LightProfile',
							() => html`
							<div class="applicationSectionNav">
								<div class="arrows">
									<button class="arrow ${classMap({active: currentIndex > 0})}"
											@click=${this.previousSection}>
										${previous}
									</button>
									<button class="arrow choice ${classMap({active: currentIndex < this.sectionConfig.length - 1 || this.featureModelIsSatisfied})}"
										@click=${this.nextSection}>
										<span class="desktop-only">${msg('Select your luminaire')}</span>
										${when(currentIndex === this.sectionConfig.length - 1 && this.featureModelIsSatisfied, () => checkmark, () => next)}
									</button>
								</div>
								${when(!selectLuminaryIsSatisfied(this.state),

									() => html`

									<button @click="${this.toggleSpecsheet}"  class="choice noLuminare ${classMap({ active: isActive })}"
									?disabled="${isButtonDisabled}">
										${msg('Continue without luminaire')}
										${when(currentIndex === this.sectionConfig.length - 1 && this.featureModelIsSatisfied, () => checkmark, () => next)}
									</button>`

									)}`,
								() => html`
							</div>`

						)}

				`)
			}

			`;
		 }

		}

